import { EditableItem, editableId } from '../constants';
import { EditableType } from '../../../constants';
import * as helpers from '../../helpers';
import { TagName, MediaElementSources, MediaElementData, MediaElementConfig, MediaElement } from '../../editableMedia/typing';
import { getVideoSettings } from '../../editableMedia/getData';

export enum MediaElementDataAttribute {
	edited = 'data-uprez-edited', // is set to media when it was modified in backoffice
	label = 'data-uprez-label', // is set to store media name
	tooltip = 'data-uprez-tooltip', // is set to store media tooltip text
	tooltipActive = 'data-uprez-tooltip-active', // is set when tooltip is active
	defaultLink = 'data-uprez-default-link', // is set to media to store default link
	driveLink = 'data-uprez-drive-link', // is set to media to store drive link
	externalLink = 'data-uprez-external-link', // is set to media to store external link
	externalLinkActive = 'data-uprez-external-link-active', // is set when external link is active
	aspectRatio = 'data-uprez-aspect-ratio', // is set to store media aspect ratio
	defaultStyle = 'data-uprez-default-style', // is set to store default media style
}

interface HtmlMediaElementData extends MediaElementData {
	frameAspectRatio?: number;
	style: string;
}

export type HtmlMediaElementConfig = HtmlMediaElementData & MediaElementConfig;

interface MediaData {
	items: HtmlMediaElementConfig[];
	isError: boolean;
}

const isImageElement = (element: HTMLElement): boolean => (
	element.hasAttribute(EditableItem.Image)
);

const isVideoElement = (element: HTMLElement): boolean => (
	element.tagName === TagName.video && element.hasAttribute(EditableItem.Video)
);

const isVideoIframeElement = (element: HTMLElement): boolean => (
	element.tagName === TagName.iframe && element.hasAttribute(EditableItem.Video)
);

// edited image element has wrapper div outside image
export const isEditedImageElement = (element: HTMLElement): boolean => (
	element.tagName === TagName.div && isImageElement(element)
);

const getMediaType = (element: MediaElement): EditableType => {
	switch (element.tagName) {
		case TagName.img:
		default:
			return EditableType.image;
		case TagName.video:
			return EditableType.video;
		case TagName.iframe:
			return element.hasAttribute(EditableItem.Video)
				? EditableType.video
				: EditableType.animation;
	}
};

const getElementSources = (element: MediaElement, isEditedFromBackoffice: boolean): MediaElementSources => {
	if (isEditedFromBackoffice) {
		return {
			defaultLink: element.getAttribute(MediaElementDataAttribute.defaultLink) || '',
			driveLink: element.getAttribute(MediaElementDataAttribute.driveLink) || '',
			externalLink: element.getAttribute(MediaElementDataAttribute.externalLink) || '',
			isExternalLinkActive: element.getAttribute(MediaElementDataAttribute.externalLinkActive) === 'true',
		};
	}

	const elementWithSrc = isVideoElement(element)
		? element.querySelector('source')
		: element;
	const { src } = elementWithSrc as MediaElement;
	const isVideoIframe = isVideoIframeElement(element);
	const defaultLink = helpers.getUrlWithoutOrigin(src, true);

	return {
		defaultLink,
		driveLink: '',
		externalLink: isVideoIframe ? defaultLink : '',
		isExternalLinkActive: isVideoIframe,
	};
};

const getMediaData = (element: MediaElement): HtmlMediaElementData => {
	const mediaElement = isEditedImageElement(element)
		? element.querySelector('img')
		: element;
	const frameAspectRatio = element.getAttribute(MediaElementDataAttribute.aspectRatio) !== null
		? +(element.getAttribute(MediaElementDataAttribute.aspectRatio) as string)
		: undefined;
	const isEditedFromBackoffice = element.getAttribute(MediaElementDataAttribute.edited) === 'true';
	const tooltipActionAttribute = element.attributes['onmouseenter'] || {}; // tslint:disable-line no-string-literal
	const { value: defaultTooltipAction } = tooltipActionAttribute;
	const { style: { width, top, left } } = mediaElement as MediaElement;

	return {
		type: getMediaType(mediaElement as MediaElement),
		width: isEditedFromBackoffice
			? helpers.getNumberFromString(width as string)
			: 0,
		height: 0,
		x: isEditedFromBackoffice
			? helpers.getNumberFromString(left as string)
			: 0,
		y: isEditedFromBackoffice
			? helpers.getNumberFromString(top as string)
			: 0,
		frameAspectRatio,
		label: element.getAttribute(MediaElementDataAttribute.label) || '',
		isEditedFromBackoffice,
		tooltip: element.getAttribute(MediaElementDataAttribute.tooltip) || helpers.getDefaultTooltip(defaultTooltipAction),
		isTooltipActive: element.getAttribute(MediaElementDataAttribute.tooltipActive) === 'true',
		canDragAndResize: isImageElement(element)
			? isEditedFromBackoffice
			: false,
		style: (isEditedFromBackoffice
			? element.getAttribute(MediaElementDataAttribute.defaultStyle)
			: element.getAttribute('style')) || '',
		videoSettings: getVideoSettings(mediaElement as MediaElement, isVideoElement(element), isVideoIframeElement(element)),
		...getElementSources(element, isEditedFromBackoffice),
	};
};

const getConfig = (element: MediaElement, page: number): HtmlMediaElementConfig => {
	const id = element.getAttribute(editableId) as string;

	return {
		page,
		id,
		importManagerId: `${id}-${page}`,
		frameWidth: 0,
		frameHeight: 0,
		isLoaded: false,
		...getMediaData(element),
	};
};

export default (page: number, htmlDocument: HTMLDocument): MediaData => {
	const items: HtmlMediaElementConfig[] = [];
	let isError: boolean = false;

	const editableMediaElements = [
		...htmlDocument.querySelectorAll(`[${EditableItem.Image}]`),
		...htmlDocument.querySelectorAll(`[${EditableItem.Video}]`),
	] as MediaElement[];

	try {
		editableMediaElements.forEach(element => (
			items.push(getConfig(element, page))
		));
	} catch (err) {
		isError = true;
	}

	return {
		items,
		isError,
	};
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/utils/htmlEditor/editableMedia/getData.ts